import { noteUpdateAttributionAtomFamily } from '@/atoms/chatAtoms';
import { useChat } from '@/components/chat/hooks/useChat';
import { useMarkdownNativeInserts } from '@/components/chat/hooks/useMarkdownNativeInserts';
import { useNoteOrDocumentInfo } from '@/components/ui/ask-ai-input/hooks/useNoteOrDocumentInfo';
import { isMarkdownNativeDocumentSurface } from '@/container/ScribeTabs/TabPane/isMarkdownNativeDocumentSurface';
import { useIsMarkdownNativeEditorEnabled } from '@/features/flags';
import { simpleMarkdownToHtml } from '@/helpers/markdownToHtml';
import { useMutateCreateDocumentFromAskAI } from '@/hooks/mutation/useMutateCreateDocumentFromAskAI';
import { useMutateSyncNoteToServer } from '@/hooks/mutation/useMutateUpdateNoteBySessionId';
import { useMutateUpdateSyncDocumentById } from '@/hooks/mutation/useMutateUpdateDocumentById';
import { useQueryDocumentById } from '@/hooks/query/useQueryDocumentById';
import {
  useDismissPendingNoteSuggestions,
  useQuerySessionById,
} from '@heidi/session/data';
import {
  TabType,
  useSessionWindow,
  useSessionWindows,
} from '@/hooks/query/useQuerySessionWindows';
import {
  askAiAddAsNewOutput,
  askAiAddToNote,
  askAiReplaceNote,
  noteInsertApplied,
} from '@/lib/analytics/scribe/ask-ai';
import { ContentType } from '@heidi/schema/ml-scribe';
import { useSetAtom } from 'jotai';
import { useCallback, useMemo } from 'react';

export const useChatResponseActions = (
  responseText: string,
  mode?: string,
  sessionId?: string | null,
  chatTitle?: string,
  chatId?: string | null,
  messageId?: string | null
) => {
  // Use empty string for sessionless chats to disable session queries
  const effectiveSessionId = sessionId ?? '';

  // Attribution atom for tracking Evidence-sourced note updates
  const setNoteAttribution = useSetAtom(noteUpdateAttributionAtomFamily(effectiveSessionId));

  const { conversation } = useChat(effectiveSessionId || null);

  // extract the last user message as the likely query that prompted this response
  const lastUserMessage = useMemo(() => {
    const userMessage = conversation.timeline
      .filter((entry) => entry.type === 'user_message')
      .pop();
    return userMessage?.type === 'user_message' ? userMessage.content : undefined;
  }, [conversation.timeline]);

  const { activeWindowIndex } = useSessionWindows({
    sessionId: effectiveSessionId,
  });
  const { activeTab } = useSessionWindow({
    sessionId: effectiveSessionId,
    windowIndex: activeWindowIndex,
  });
  const tabName = activeTab?.name;
  const documentId = activeTab?.documentId ?? '';

  const { data: currentSession } = useQuerySessionById({
    sessionId: effectiveSessionId,
    select: (data) => {
      if (!data) return undefined;
      return {
        consult_note: data.consult_note,
      };
    },
  });

  const { data: currentDocument } = useQueryDocumentById({
    sessionId: effectiveSessionId,
    documentId,
  });

  // The markdown-native editor stores raw markdown in whichever tab it renders.
  const isMarkdownNative = useIsMarkdownNativeEditorEnabled();

  const isMarkdownNativeDocument = isMarkdownNativeDocumentSurface(
    isMarkdownNative,
    currentDocument?.document_tab_type
  );

  // Markdown insert/replace content building + editor force-apply signalling
  // for the markdown-native editors (note keyed by session, document by ID).
  const {
    appendNoteMarkdown,
    replaceNoteMarkdown,
    appendDocumentMarkdown,
    replaceDocumentMarkdown,
    newDocumentMarkdown,
  } = useMarkdownNativeInserts({
    sessionId: effectiveSessionId,
    documentId,
    responseText,
  });

  const { mutate: createNewDocument } =
    useMutateCreateDocumentFromAskAI(effectiveSessionId);
  const { mutate: updateNote } = useMutateSyncNoteToServer({
    contentTypeOverride: isMarkdownNative ? ContentType.MARKDOWN : undefined,
  });
  const { mutate: updateDocument } = useMutateUpdateSyncDocumentById({
    contentTypeOverride: isMarkdownNativeDocument ? ContentType.MARKDOWN : undefined,
  });

  const { brain } = useNoteOrDocumentInfo();

  // Replace Note swaps the underlying text, invalidating the BE-supplied char
  // offsets on any pending note suggestions. The editor's `update` event is
  // suppressed when content is synced from the server (emitUpdate: false), so
  // we dismiss imperatively here rather than relying on the on-edit listener.
  const dismissPendingNoteSuggestions =
    useDismissPendingNoteSuggestions(effectiveSessionId);

  const onAddNewTab = useCallback(() => {
    // get the original user query, fallback to response text slice if no user messages
    const aiCommandText = lastUserMessage || responseText.slice(0, 50) || 'Chat Response';

    // New documents render markdown-native under the flag, so store markdown;
    // otherwise fall back to the converted HTML the legacy editors expect.
    createNewDocument({
      content: isMarkdownNative
        ? newDocumentMarkdown()
        : simpleMarkdownToHtml({ markdown: responseText }),
      contentType: isMarkdownNative ? ContentType.MARKDOWN : undefined,
      aiCommandText,
      brain,
      name: chatTitle || responseText.slice(0, 50) || 'Chat Response',
    });

    // analytics tracking
    askAiAddAsNewOutput(mode);
    noteInsertApplied({
      insertAction: 'add_as_new_output',
      editorVariant: isMarkdownNative ? 'markdown_native' : 'html',
      sessionId: effectiveSessionId,
      mode,
      chatId,
      messageId,
    });
  }, [
    responseText,
    createNewDocument,
    brain,
    lastUserMessage,
    mode,
    chatTitle,
    effectiveSessionId,
    isMarkdownNative,
    newDocumentMarkdown,
    chatId,
    messageId,
  ]);

  // TODO: refactor add to note and replace note since they are effectively the
  // TODO: same logic; i.e. generic update function w. boolean for replace/add
  const onAddToNote = useCallback(() => {
    // convert markdown to HTML for consistency with documents
    const htmlContent = simpleMarkdownToHtml({
      markdown: responseText,
    });

    // Only set attribution for note tabs (not documents) to prevent misattribution
    const isNoteTab =
      tabName === TabType.NOTE ||
      tabName === TabType.CONTEXT ||
      tabName === TabType.TRANSCRIPT;

    if (isNoteTab && effectiveSessionId && mode === 'evidence') {
      setNoteAttribution({
        source: 'evidence',
        updateType: 'add',
        timestamp: Date.now(),
      });
    }

    switch (tabName) {
      case TabType.NOTE:
      case TabType.CONTEXT:
      case TabType.TRANSCRIPT: {
        const existingNote = currentSession?.consult_note.result || '';
        // Markdown-native appends markdown; HTML editors store converted HTML.
        const consultNote = isMarkdownNative
          ? appendNoteMarkdown(existingNote)
          : `${existingNote}\n${htmlContent}`;
        updateNote({
          consultNote,
          sessionId: effectiveSessionId,
        });
        break;
      }
      case TabType.DOCUMENT:
        if (documentId) {
          const existingDoc = currentDocument?.content || '';
          const content = isMarkdownNativeDocument
            ? appendDocumentMarkdown(existingDoc)
            : `${existingDoc}\n${htmlContent}`;
          updateDocument({
            content,
            sessionId: effectiveSessionId,
            documentId,
          });
        }
        break;
      default:
        break;
    }

    // Analytics tracking
    askAiAddToNote(mode);
    // Only the note tabs and a document tab with a live document actually wrote
    // an insert; skip the rollout event for no-op paths so the metric is clean.
    const didInsert = isNoteTab || (tabName === TabType.DOCUMENT && Boolean(documentId));
    if (didInsert) {
      noteInsertApplied({
        insertAction: 'add_to_note',
        editorVariant: (isNoteTab ? isMarkdownNative : isMarkdownNativeDocument)
          ? 'markdown_native'
          : 'html',
        sessionId: effectiveSessionId,
        mode,
        chatId,
        messageId,
      });
    }
  }, [
    responseText,
    currentDocument?.content,
    currentSession?.consult_note.result,
    documentId,
    effectiveSessionId,
    isMarkdownNative,
    isMarkdownNativeDocument,
    tabName,
    updateDocument,
    updateNote,
    mode,
    setNoteAttribution,
    appendNoteMarkdown,
    appendDocumentMarkdown,
    chatId,
    messageId,
  ]);

  const onReplaceNote = useCallback(() => {
    // Convert markdown to HTML for consistency with documents
    const htmlContent = simpleMarkdownToHtml({
      markdown: responseText,
    });

    // Only set attribution for note tabs (not documents) to prevent misattribution
    const isNoteTab =
      tabName === TabType.NOTE ||
      tabName === TabType.CONTEXT ||
      tabName === TabType.TRANSCRIPT;

    if (isNoteTab && effectiveSessionId && mode === 'evidence') {
      setNoteAttribution({
        source: 'evidence',
        updateType: 'replace',
        timestamp: Date.now(),
      });
    }

    switch (tabName) {
      case TabType.TRANSCRIPT:
      case TabType.NOTE:
      case TabType.CONTEXT: {
        dismissPendingNoteSuggestions();
        // Markdown-native stores markdown; HTML editors store the HTML.
        const consultNote = isMarkdownNative ? replaceNoteMarkdown() : htmlContent || '';
        updateNote({
          consultNote,
          sessionId: effectiveSessionId,
        });
        break;
      }
      case TabType.DOCUMENT:
        if (documentId) {
          const content = isMarkdownNativeDocument
            ? replaceDocumentMarkdown()
            : htmlContent || '';
          updateDocument({
            content,
            sessionId: effectiveSessionId,
            documentId,
          });
        }
        break;
      default:
        break;
    }

    // Analytics tracking
    askAiReplaceNote(mode);
    // Only the note tabs and a document tab with a live document actually wrote
    // an insert; skip the rollout event for no-op paths so the metric is clean.
    const didInsert = isNoteTab || (tabName === TabType.DOCUMENT && Boolean(documentId));
    if (didInsert) {
      noteInsertApplied({
        insertAction: 'replace_note',
        editorVariant: (isNoteTab ? isMarkdownNative : isMarkdownNativeDocument)
          ? 'markdown_native'
          : 'html',
        sessionId: effectiveSessionId,
        mode,
        chatId,
        messageId,
      });
    }
  }, [
    responseText,
    documentId,
    effectiveSessionId,
    isMarkdownNative,
    isMarkdownNativeDocument,
    tabName,
    updateDocument,
    updateNote,
    mode,
    setNoteAttribution,
    replaceNoteMarkdown,
    replaceDocumentMarkdown,
    dismissPendingNoteSuggestions,
    chatId,
    messageId,
  ]);

  // Get a display name for the active tab
  const activeTabName = useMemo(() => {
    switch (tabName) {
      case TabType.NOTE:
        return 'Note';
      case TabType.DOCUMENT:
        return currentDocument?.name || 'Document';
      case TabType.CONTEXT:
        return 'Context';
      case TabType.TRANSCRIPT:
        return 'Transcript';
      default:
        return undefined;
    }
  }, [tabName, currentDocument?.name]);

  return { onAddNewTab, onAddToNote, onReplaceNote, activeTabName };
};
